// src/context/ClinicContext.jsx
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axiosInstance, { setAxiosClinicId } from '../axiosInstance';
import { useAuth } from './AuthContext';

const ClinicContext = createContext(null);

export const ClinicProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState(null);

  // Clave por usuario para recordar selección
  const storageKey = useMemo(() => {
    const uid = user?.pk || 'anon';
    return `clinic:selected:${uid}`;
  }, [user?.pk]);

  const fetchMemberships = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/clinics/my-memberships/', {
        meta: { skipClinicHeader: true } // <- Nunca enviar X-Clinic-Id aquí
      });
      const arr = Array.isArray(data) ? data : [];
      setMemberships(arr);
      return arr;
    } catch {
      setMemberships([]);
      return [];
    }
  }, []);

  const refreshMemberships = useCallback(async () => {
    return await fetchMemberships();
  }, [fetchMemberships]);

  // Cargar membresías y restaurar selección PREVIA (sin auto-selección)
  useEffect(() => {
    let abort = false;
    (async () => {
      // Cambio de usuario o no autenticado: limpiar todo
      if (!isAuthenticated) {
        setMemberships([]);
        setSelectedClinicId(null);
        setAxiosClinicId(null);
        return;
      }

      const arr = await fetchMemberships();
      if (abort) return;

      try {
        // Restaurar la selección guardada si sigue siendo válida
        const saved = localStorage.getItem(storageKey);
        const savedId = saved && saved !== 'null' ? Number(saved) : null;

        const exists = savedId && arr.some(m => Number(m?.clinica?.id) === Number(savedId));
        if (exists) {
          setSelectedClinicId(savedId);
        } else {
          // Si no hay guardado válido, permanecer en modo individual
          setSelectedClinicId(null);
        }
      } catch {
        // En caso de error al leer storage, permanecer en individual
        setSelectedClinicId(null);
      }
    })();

    return () => { abort = true; };
  }, [isAuthenticated, fetchMemberships, storageKey]);

  // Persistir selección por usuario
  useEffect(() => {
    try {
      if (selectedClinicId && selectedClinicId !== 0) {
        localStorage.setItem(storageKey, String(selectedClinicId));
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch {}
  }, [selectedClinicId, storageKey]);

  // Sincronizar con axios: controla X-Clinic-Id globalmente
  useEffect(() => {
    setAxiosClinicId(selectedClinicId ?? null);
  }, [selectedClinicId]);

  // Objeto de clínica activa
  const activeClinic = useMemo(() => {
    if (!selectedClinicId) return null;
    return memberships.find(m => Number(m?.clinica?.id) === Number(selectedClinicId)) || null;
    // Si no se encuentra, retornará null (evita inconsistencias)
  }, [memberships, selectedClinicId]);

  return (
    <ClinicContext.Provider
      value={{
        memberships,
        selectedClinicId,
        setSelectedClinicId,
        refreshMemberships,
        activeClinic,
      }}
    >
      {children}
    </ClinicContext.Provider>
  );
};

export const useClinic = () => {
  const ctx = useContext(ClinicContext);
  if (!ctx) throw new Error('useClinic debe usarse dentro de ClinicProvider');
  return ctx;
};
